import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CheckCircle, Eraser, PenNib, Spinner, SealCheck, CaretDown } from "@phosphor-icons/react";
import SignaturePad from "@/components/SignaturePad";
import { fmtDate, portalErr } from "@/lib/portalApi";

export default function PortalUnterschrift({ api, onSigned }) {
  const [cfg, setCfg] = useState(null);

  const load = async () => {
    try { const r = await api.get("/portal/unterschrift"); setCfg(r.data); }
    catch (e) { toast.error(portalErr(e, "Dokumente konnten nicht geladen werden")); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!cfg) return <div className="flex items-center gap-3 text-[#6B7280] py-10 justify-center"><Spinner size={20} className="animate-spin" /> Lädt …</div>;

  const offen = cfg.dokumente.filter((d) => !d.unterschrieben_am);
  const fertig = cfg.dokumente.filter((d) => d.unterschrieben_am);

  return (
    <div className="space-y-6" data-testid="portal-unterschrift">
      <div>
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#9CA3AF]">Digital unterschreiben</p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight mt-1">{cfg.modus === "vollmacht" ? "Vollmacht" : "Vollmacht & Abtretung"}</h1>
        <p className="text-sm text-[#6B7280] mt-2">Damit {cfg.kanzlei?.name || "Ihre Kanzlei"} für Sie tätig werden kann, benötigen wir Ihre Unterschrift. Das geht hier bequem digital — rechtssicher protokolliert.</p>
      </div>

      {cfg.alle_unterschrieben && (
        <div className="bg-[#DCFCE7] border border-[#86EFAC] rounded-2xl p-5 flex items-center gap-4" data-testid="portal-sign-done">
          <SealCheck size={36} weight="duotone" className="text-[#15803D] shrink-0" />
          <div>
            <p className="font-medium text-[#14532D]">Alles unterschrieben — vielen Dank!</p>
            <p className="text-sm text-[#166534]">Ihre unterschriebenen Dokumente finden Sie unter „Dokumente“.</p>
          </div>
        </div>
      )}

      {offen.map((d) => (
        <SignCard key={d.key} doc={d} cfg={cfg} api={api} onDone={() => { load(); onSigned?.(); }} />
      ))}

      {fertig.length > 0 && (
        <section className="bg-white rounded-2xl border border-[#E5E7EB] p-5 sm:p-7" data-testid="portal-sign-fertig">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#9CA3AF] mb-3">Bereits unterschrieben</p>
          <ul className="divide-y divide-[#F3F4F6]">
            {fertig.map((d) => (
              <li key={d.key} className="flex items-center gap-3 py-3" data-testid={`portal-signed-${d.key}`}>
                <CheckCircle size={22} weight="fill" className="text-[#16A34A]" />
                <span className="flex-1 font-medium">{d.titel}</span>
                <span className="text-xs text-[#6B7280] font-mono">{fmtDate(d.unterschrieben_am, true)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function SignCard({ doc, cfg, api, onDone }) {
  const padRef = useRef(null);
  const [hasSig, setHasSig] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const m = cfg.mandant || {};
  const k = cfg.kanzlei || {};

  const submit = async () => {
    if (padRef.current?.isEmpty()) { toast.error("Bitte zuerst unterschreiben"); return; }
    if (!confirmed) { toast.error("Bitte bestätigen Sie, dass Sie den Text gelesen haben"); return; }
    setBusy(true);
    try {
      await api.post("/portal/unterschrift", { dokument: doc.key, signature_data_url: padRef.current.toDataURL() });
      toast.success(`${doc.titel} erfolgreich unterschrieben`);
      onDone();
    } catch (e) { toast.error(portalErr(e, "Unterschrift konnte nicht übermittelt werden")); }
    finally { setBusy(false); }
  };

  return (
    <section className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm overflow-hidden" data-testid={`portal-sign-card-${doc.key}`}>
      <div className="p-5 sm:p-7 border-b border-[#F3F4F6]">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#D32F2F]">Zur Unterschrift</p>
            <h2 className="font-serif text-3xl mt-1">{doc.titel}</h2>
          </div>
          {cfg.aktenzeichen && <span className="font-mono text-xs bg-[#F4F3EF] rounded-md px-2.5 py-1">Az. {cfg.aktenzeichen}</span>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5 text-sm">
          <div className="bg-[#F9FAFB] rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-[0.16em] text-[#9CA3AF] mb-1">Vollmachtgeber</p>
            <p className="font-medium">{[m.vorname, m.nachname].filter(Boolean).join(" ") || "—"}</p>
            {m.strasse && <p className="text-[#4B5563]">{m.strasse}</p>}
            {(m.plz || m.ort) && <p className="text-[#4B5563]">{[m.plz, m.ort].filter(Boolean).join(" ")}</p>}
          </div>
          <div className="bg-[#F9FAFB] rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-[0.16em] text-[#9CA3AF] mb-1">Bevollmächtigte Kanzlei</p>
            <p className="font-medium">{k.name || "—"}</p>
            {k.anwalt && <p className="text-[#4B5563]">{k.anwalt}</p>}
            {(k.adresse || k.ort) && <p className="text-[#4B5563]">{[k.adresse, [k.plz, k.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</p>}
          </div>
        </div>
        <div className="mt-5">
          <div className={`relative text-sm text-[#374151] leading-relaxed whitespace-pre-line font-serif text-[15px] ${expanded ? "" : "max-h-40 overflow-hidden"}`} data-testid={`portal-sign-text-${doc.key}`}>
            {doc.text}
            {!expanded && <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent" />}
          </div>
          <button type="button" onClick={() => setExpanded((v) => !v)} className="mt-2 text-xs font-medium text-[#1A2238] inline-flex items-center gap-1" data-testid={`portal-sign-expand-${doc.key}`}>
            <CaretDown size={14} className={`transition-transform ${expanded ? "rotate-180" : ""}`} /> {expanded ? "Weniger anzeigen" : "Vollständigen Text lesen"}
          </button>
        </div>
      </div>

      <div className="p-5 sm:p-7 space-y-4 bg-[#FAFAF8]">
        <div className="flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#9CA3AF] inline-flex items-center gap-1.5"><PenNib size={14} weight="duotone" /> Ihre Unterschrift</p>
          <button type="button" onClick={() => padRef.current?.clear()} className="text-xs text-[#6B7280] inline-flex items-center gap-1 hover:text-[#0A192F]" data-testid={`portal-sign-clear-${doc.key}`}><Eraser size={14} /> Löschen</button>
        </div>
        <SignaturePad ref={padRef} height={200} onChange={setHasSig} testId={`portal-sign-pad-${doc.key}`} />
        <label className="flex items-start gap-3 text-sm text-[#374151] cursor-pointer">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#1A2238]" data-testid={`portal-sign-confirm-${doc.key}`} />
          <span>Ich habe den Text gelesen und erteile hiermit die {doc.titel} an {k.name || "die Kanzlei"}. Meine elektronische Unterschrift gilt als verbindlich.</span>
        </label>
        <Button onClick={submit} disabled={!hasSig || !confirmed || busy} className="w-full h-12 rounded-xl bg-[#1A2238] hover:bg-[#2C3859] text-base" data-testid={`portal-sign-submit-${doc.key}`}>
          {busy ? <Spinner size={18} className="animate-spin mr-2" /> : <CheckCircle size={18} className="mr-2" weight="duotone" />} Jetzt verbindlich unterschreiben
        </Button>
        <p className="text-[11px] text-[#9CA3AF] text-center">Zeitpunkt, Zugangslink und Gerätekennung werden zur Nachweisbarkeit protokolliert.</p>
      </div>
    </section>
  );
}
